import { formatRupiah } from "@/core/helpers/string";
import { Badge } from "@k3-tech/react-kit";
import { useMenuGroupProductAddons } from "../usecases/useMenuGroupProductAddons";

export interface GroupProductAddonListProps {
	// Paused when undefined, so callers pass the id only when the section is visible.
	addonGroupID?: string | null;
	compact?: boolean;
}

// How many distinct picks Grab/POS allows for one addon, mirroring the menu build.
const maxSelect = (
	maxQty: number | null | undefined,
	isMultiple: boolean,
	componentCount: number,
) => {
	if (maxQty && maxQty > 0) return Math.min(maxQty, componentCount);
	return isMultiple ? componentCount : 1;
};

export function GroupProductAddonList({
	addonGroupID,
	compact,
}: GroupProductAddonListProps) {
	const { items, fetching } = useMenuGroupProductAddons(
		addonGroupID ?? undefined,
	);

	if (fetching) {
		return (
			<div className="py-3 text-center text-slate-400 text-sm">
				Loading add-ons…
			</div>
		);
	}
	if (items.length === 0) {
		return (
			<div className="py-3 text-center text-slate-400 text-sm">
				No add-ons configured.
			</div>
		);
	}

	return (
		<div className={compact ? "space-y-2" : "space-y-3"}>
			{items.map((item) => {
				const addon = item.productAddon;
				const components = addon.components ?? [];
				return (
					<div
						key={item.productAddonGroupItemID}
						className="rounded-lg border border-slate-200 p-3"
					>
						<div className="flex flex-wrap items-center justify-between gap-2">
							<span className="font-medium text-sm">{addon.name}</span>
							<Badge variant="secondary" className="font-normal text-[11px]">
								{`${addon.isRequired ? "Required" : "Optional"} · max ${maxSelect(
									addon.maxQty,
									addon.isMultiple,
									components.length,
								)}`}
							</Badge>
						</div>
						<div className="mt-2 space-y-1">
							{components.map((component) => (
								<div
									key={component.productAddonComponentID}
									className="flex items-center justify-between text-sm"
								>
									<span className="text-slate-600">
										{component.productName}
									</span>
									<span className="text-slate-500">
										{/* Fixed number only for custom; pricelist resolves per branch, so never a number here. */}
										{component.priceType === "custom"
											? `+${formatRupiah(component.customSellingPrice)}`
											: "Pricelist · per branch"}
									</span>
								</div>
							))}
						</div>
					</div>
				);
			})}
		</div>
	);
}
